import { Show, createSignal } from "solid-js";
import { Modal } from "~/ui/Modal";
import type { ClassDraft, TeacherClass } from "./classes";

export function ClassFormModal(props: {
  value?: TeacherClass;
  onClose: () => void;
  onSave: (draft: ClassDraft) => Promise<void>;
}) {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [pending, setPending] = createSignal<ClassDraft | null>(null);

  const submit = (event: SubmitEvent) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget as HTMLFormElement);
    const name = String(values.get("name") ?? "").trim();
    if (!name) return setError("Klassenname erforderlich");
    setError("");
    const draft: ClassDraft = {
      name,
      description: String(values.get("description") ?? "").trim(),
      schoolYear: String(values.get("schoolYear") ?? "").trim()
    };
    const nameChanged = props.value && name !== props.value.name;
    if (nameChanged) {
      setPending(draft);
    } else {
      void save(draft);
    }
  };

  const save = async (draft: ClassDraft) => {
    setBusy(true);
    setError("");
    try {
      await props.onSave(draft);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Fehler beim Speichern");
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={props.value ? "Klasse bearbeiten" : "Neue Klasse erstellen"} onClose={props.onClose}>
      <Show when={pending()} fallback={
        <form onSubmit={submit}>
          <div class="form-group">
            <label class="form-label" for="class-name">Klassenname *</label>
            <input name="name" id="class-name" class="form-input" value={props.value?.name ?? ""} placeholder="z.B. 8a Mathematik" maxlength={100} autofocus />
          </div>
          <div class="form-group">
            <label class="form-label" for="class-description">Beschreibung</label>
            <textarea name="description" id="class-description" class="form-textarea" placeholder="Optional">{props.value?.description ?? ""}</textarea>
          </div>
          <div class="form-group">
            <label class="form-label" for="class-year">Schuljahr</label>
            <input name="schoolYear" id="class-year" class="form-input" value={props.value?.school_year ?? ""} placeholder="z.B. 2024/25" />
          </div>
          {error() && <div class="form-error" style={{ "margin-bottom": "12px" }} role="alert">{error()}</div>}
          <button class="btn btn-primary btn-full" type="submit" disabled={busy()}>
            {props.value ? "Speichern" : "Klasse erstellen"}
          </button>
        </form>
      }>
        {(draft) => (
          <div>
            <p style={{ "margin-bottom": "16px" }}>Klassenname wirklich ändern?</p>
            <div style={{ background: "var(--surface-2, #f4f4f5)", "border-radius": "8px", padding: "12px 16px", "margin-bottom": "20px", "font-size": "0.9rem" }}>
              <div style={{ display: "flex", "align-items": "center", gap: "10px" }}>
                <span style={{ color: "var(--text-muted)", "text-decoration": "line-through" }}>{props.value!.name}</span>
                <span style={{ color: "var(--text-muted)" }}>→</span>
                <strong>{draft().name}</strong>
              </div>
            </div>
            {error() && <div class="form-error" style={{ "margin-bottom": "12px" }} role="alert">{error()}</div>}
            <div style={{ display: "flex", gap: "8px" }}>
              <button class="btn btn-primary" type="button" disabled={busy()} onClick={() => void save(draft())}>
                {busy() ? "Wird gespeichert…" : "Ja, umbenennen"}
              </button>
              <button class="btn btn-secondary" type="button" disabled={busy()} onClick={() => setPending(null)}>
                Zurück
              </button>
            </div>
          </div>
        )}
      </Show>
    </Modal>
  );
}
